import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, ArrowsClockwise, Warning, CaretDown, CaretRight } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import AttachmentsPanel from "../components/AttachmentsPanel";
import { StandardReports } from "../components/StandardReports";

const TABS = ["Incidents", "Hazards", "Permits to Work", "Reports"];

export default function HSEPage() {
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "Incidents");
  const [summary, setSummary] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [hazards, setHazards] = useState([]);
  const [permits, setPermits] = useState([]);

  const refresh = async () => {
    try {
      const [s, i, h, p] = await Promise.all([
        api.get("/hse/summary").then((r) => r.data),
        api.get("/hse/incidents").then((r) => r.data),
        api.get("/hse/hazards").then((r) => r.data),
        api.get("/hse/permits").then((r) => r.data),
      ]);
      setSummary(s); setIncidents(i); setHazards(h); setPermits(p);
    } catch (e) {
      if (e.response?.status === 402) toast.error("HSE module requires Enterprise plan or higher.");
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <div className="p-6 sm:p-8" data-testid="hse-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / 06 · HEALTH, SAFETY, ENVIRONMENT</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Safety.</h1>
        </div>
        <button onClick={refresh} className="text-xs font-mono text-zinc-500 hover:text-zinc-950 flex items-center gap-1">
          <ArrowsClockwise size={12} /> REFRESH
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 border border-zinc-200 mb-6">
          <div className="border-r border-zinc-200 px-6 py-4">
            <div className="label-sm text-zinc-500">Incidents</div>
            <div className="font-display font-black text-2xl tnum mt-1">{summary.total_incidents}</div>
            <div className="text-[10px] text-zinc-500">{summary.open_incidents} open</div>
          </div>
          <div className="border-r border-zinc-200 px-6 py-4">
            <div className="label-sm text-zinc-500">Critical</div>
            <div className="font-display font-black text-2xl tnum mt-1 text-[#D0021B]">{summary.critical_incidents}</div>
          </div>
          <div className="border-r border-zinc-200 px-6 py-4">
            <div className="label-sm text-zinc-500">Hazards</div>
            <div className="font-display font-black text-2xl tnum mt-1">{summary.open_hazards}</div>
            <div className="text-[10px] text-zinc-500">of {summary.total_hazards} tracked</div>
          </div>
          <div className="px-6 py-4">
            <div className="label-sm text-zinc-500">Active Permits</div>
            <div className="font-display font-black text-2xl tnum mt-1">{summary.active_permits}</div>
          </div>
        </div>
      )}

      <div className="flex border-b border-zinc-950 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`hse-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-xs font-semibold tracking-[0.1em] uppercase border-r border-zinc-950 transition-colors ${
              tab === t ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="border-x border-b border-zinc-950">
        {tab === "Incidents" && <Incidents incidents={incidents} onChange={refresh} />}
        {tab === "Hazards" && <Hazards hazards={hazards} onChange={refresh} />}
        {tab === "Permits to Work" && <Permits permits={permits} onChange={refresh} />}
        {tab === "Reports" && <StandardReports module="hse" />}
      </div>
    </div>
  );
}

const sevColor = (s) => ({
  low: "bg-zinc-100 text-zinc-700",
  medium: "bg-amber-50 text-amber-700",
  high: "bg-orange-50 text-orange-700",
  critical: "bg-red-50 text-[#D0021B]",
}[s] || "bg-zinc-100");

function Incidents({ incidents, onChange }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ title: "", severity: "low", location: "", description: "" });
  const [expanded, setExpanded] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/hse/incidents", form);
      toast.success("Incident reported");
      setForm({ title: "", severity: "low", location: "", description: "" });
      onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setStatus = async (id, s) => {
    await api.patch(`/hse/incidents/${id}?status=${s}`);
    toast.success(`Status: ${s}`);
    onChange();
  };

  const canAttachTo = (incident) => user?.role === "admin" || incident.reported_by === user?.email;

  return (
    <div>
      <form onSubmit={create} className="grid grid-cols-2 md:grid-cols-[1fr_140px_160px_1fr_auto] gap-px bg-zinc-200 border-b border-zinc-950" data-testid="incident-form">
        <input value={form.title} onChange={(e) => set("title", e.target.value)} required placeholder="Incident title" data-testid="incident-title-input" className="bg-white px-3 py-3 text-sm outline-none" />
        <select value={form.severity} onChange={(e) => set("severity", e.target.value)} data-testid="incident-severity-select" className="bg-white px-3 py-3 text-sm outline-none">
          {["low", "medium", "high", "critical"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <input value={form.location} onChange={(e) => set("location", e.target.value)} required placeholder="Location" className="bg-white px-3 py-3 text-sm outline-none" />
        <input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" className="bg-white px-3 py-3 text-sm outline-none" />
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 hover:bg-[#FF3E00] flex items-center justify-center gap-1" data-testid="incident-create-btn">
          <Plus size={14} /> REPORT
        </button>
      </form>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">Ref</th>
            <th className="text-left px-4 py-2.5">Title</th>
            <th className="text-left px-4 py-2.5">Severity</th>
            <th className="text-left px-4 py-2.5">Location</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="w-32" />
          </tr>
        </thead>
        <tbody data-testid="incidents-tbody">
          {incidents.map((i) => (
            <Fragment key={i.id}>
              <tr className="border-b border-zinc-100 hover:bg-zinc-50">
                <td className="px-4 py-2.5 tnum text-xs">
                  <button
                    onClick={() => setExpanded(expanded === i.id ? null : i.id)}
                    className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-950"
                    data-testid={`incident-expand-${i.id}`}
                    aria-label={expanded === i.id ? "Collapse" : "Expand"}
                  >
                    {expanded === i.id ? <CaretDown size={10} /> : <CaretRight size={10} />}
                    {i.ref}
                  </button>
                </td>
                <td className="px-4 py-2.5 font-medium">{i.title}</td>
                <td className="px-4 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 ${sevColor(i.severity)}`}>{i.severity?.toUpperCase()}</span></td>
                <td className="px-4 py-2.5 text-zinc-600">{i.location}</td>
                <td className="px-4 py-2.5"><span className="text-[10px] px-1.5 py-0.5 bg-zinc-100">{i.status?.toUpperCase()}</span></td>
                <td className="px-4 py-2.5">
                  {i.status !== "resolved" && (
                    <button onClick={() => setStatus(i.id, "resolved")} className="text-[10px] font-mono text-emerald-700 hover:underline" data-testid={`incident-resolve-${i.id}`}>RESOLVE</button>
                  )}
                </td>
              </tr>
              {expanded === i.id && (
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <td colSpan={6} className="px-4 py-4">
                    <AttachmentsPanel
                      parentType="incident"
                      parentId={i.id}
                      canAttach={canAttachTo(i)}
                      currentUser={user}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Hazards({ hazards, onChange }) {
  const [form, setForm] = useState({ description: "", risk_level: "medium", location: "" });

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/hse/hazards", form);
      toast.success("Hazard logged");
      setForm({ description: "", risk_level: "medium", location: "" });
      onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setStatus = async (id, s) => {
    await api.patch(`/hse/hazards/${id}?status=${s}`);
    onChange();
  };

  return (
    <div>
      <form onSubmit={create} className="grid grid-cols-2 md:grid-cols-[2fr_140px_1fr_auto] gap-px bg-zinc-200 border-b border-zinc-950" data-testid="hazard-form">
        <input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required placeholder="Hazard description" data-testid="hazard-desc-input" className="bg-white px-3 py-3 text-sm outline-none" />
        <select value={form.risk_level} onChange={(e) => setForm({...form, risk_level: e.target.value})} className="bg-white px-3 py-3 text-sm outline-none">
          {["low", "medium", "high"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} required placeholder="Location" className="bg-white px-3 py-3 text-sm outline-none" />
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 hover:bg-[#FF3E00] flex items-center justify-center gap-1" data-testid="hazard-create-btn">
          <Plus size={14} /> LOG
        </button>
      </form>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">Ref</th>
            <th className="text-left px-4 py-2.5">Description</th>
            <th className="text-left px-4 py-2.5">Risk</th>
            <th className="text-left px-4 py-2.5">Location</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="w-32" />
          </tr>
        </thead>
        <tbody data-testid="hazards-tbody">
          {hazards.map((h) => (
            <tr key={h.id} className="border-b border-zinc-100">
              <td className="px-4 py-2.5 tnum text-xs">{h.ref}</td>
              <td className="px-4 py-2.5">{h.description}</td>
              <td className="px-4 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 ${sevColor(h.risk_level)}`}>{h.risk_level?.toUpperCase()}</span></td>
              <td className="px-4 py-2.5 text-zinc-600">{h.location}</td>
              <td className="px-4 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 ${h.status === "mitigated" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{h.status?.toUpperCase()}</span></td>
              <td className="px-4 py-2.5">
                {h.status === "open" && (
                  <button onClick={() => setStatus(h.id, "mitigated")} className="text-[10px] font-mono text-emerald-700 hover:underline">MITIGATE</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Permits({ permits, onChange }) {
  const [form, setForm] = useState({ work_type: "", location: "", contractor: "", start_date: "", end_date: "", risk_assessment: "" });

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/hse/permits", form);
      toast.success("Permit requested");
      setForm({ work_type: "", location: "", contractor: "", start_date: "", end_date: "", risk_assessment: "" });
      onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setStatus = async (id, s) => {
    await api.patch(`/hse/permits/${id}?status=${s}`);
    toast.success(`Permit ${s}`);
    onChange();
  };

  return (
    <div>
      <form onSubmit={create} className="p-5 border-b border-zinc-950 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="permit-form">
        <input value={form.work_type} onChange={(e) => setForm({...form, work_type: e.target.value})} required placeholder="Work type (e.g. Hot work)" className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none" />
        <input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} required placeholder="Location" className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none" />
        <input value={form.contractor} onChange={(e) => setForm({...form, contractor: e.target.value})} required placeholder="Contractor" className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none" />
        <input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} required className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none tnum" />
        <input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} required className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none tnum" />
        <input value={form.risk_assessment} onChange={(e) => setForm({...form, risk_assessment: e.target.value})} required placeholder="Risk assessment summary" className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none" />
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2 hover:bg-[#FF3E00] flex items-center justify-center gap-1 col-span-2 sm:col-span-3" data-testid="permit-create-btn">
          <Plus size={14} /> REQUEST PERMIT
        </button>
      </form>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">Ref</th>
            <th className="text-left px-4 py-2.5">Work Type</th>
            <th className="text-left px-4 py-2.5">Contractor</th>
            <th className="text-left px-4 py-2.5">Period</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="w-44" />
          </tr>
        </thead>
        <tbody data-testid="permits-tbody">
          {permits.map((p) => (
            <tr key={p.id} className="border-b border-zinc-100">
              <td className="px-4 py-2.5 tnum text-xs">{p.ref}</td>
              <td className="px-4 py-2.5">{p.work_type}</td>
              <td className="px-4 py-2.5 text-zinc-600">{p.contractor}</td>
              <td className="px-4 py-2.5 tnum text-xs">{p.start_date} → {p.end_date}</td>
              <td className="px-4 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 ${p.status === "approved" ? "bg-emerald-50 text-emerald-700" : p.status === "rejected" ? "bg-red-50 text-[#D0021B]" : "bg-amber-50 text-amber-700"}`}>{p.status?.toUpperCase()}</span></td>
              <td className="px-4 py-2.5">
                {p.status === "pending" && (
                  <div className="flex gap-2">
                    <button onClick={() => setStatus(p.id, "approved")} className="text-[10px] font-mono text-emerald-700 hover:underline">APPROVE</button>
                    <button onClick={() => setStatus(p.id, "rejected")} className="text-[10px] font-mono text-[#D0021B] hover:underline">REJECT</button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
